import { httpRequest } from '@/api/HttpRequest';
import produce from 'immer';
import { create, StateCreator, StoreApi } from 'zustand';
import { devtools } from "zustand/middleware";
import { AddToCartTicketType, CartDefaultState, CartTicket, CartType } from '../types/CartType';
import { GoogleDataLayer } from '@/components/Analytics/GoogleDataLayer/GoogleDataLayer';

type State = {
  cart: CartType;
  cartMetaTotalCount: number;
  cartMetaTotalAmmount: string;
  ticketQtys: number[];
  cartLoading: boolean;
  error: boolean;
  getCart: () => Promise<CartType | undefined>;
  addToCart: (tickets: Array<AddToCartTicketType>) => Promise<void>;
  setTicketQty: (cartItemId: string, newQty: number) => Promise<void>;
  removeAllFromCart: (ticket: Array<string>, dateOrYearStr?: string) => Promise<void>;
  removeCartItem: (ticketId: string, dateId: string | null, qty: number) => Promise<void>;
  clearCart: () => Promise<void>;
};

const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? '';
const googleDataLayer = new GoogleDataLayer

const setLoadingFunction = (set: StoreApi<State>['setState'], loading: boolean) => set((state) => ({ ...state, cartLoading: loading }));
const updateCartFunction = (set: StoreApi<State>['setState'], cart: CartType) => set((state) => ({ ...state, cart }));
const setErrorFunction = (set: StoreApi<State>['setState'], error: boolean) => set((state) => ({ ...state, error }));
const updateMetaTotalFunction = (set: StoreApi<State>['setState'], total: number) => set(produce((state) => { state.cartMetaTotalCount = total; }));
const updateCartMetaTotalAmmountFunction = (set: StoreApi<State>['setState'], total: string) => set(produce((state) => { state.cartMetaTotalAmmount = total; }));
const updateSingleTicketQtyFunction = (set: StoreApi<State>['setState'], qty: number[]) => set((state) => ({ ...state, ticketQtys: qty }));


const useCartStore = create<State>(

  devtools<State>((set) => {

  const stateActions = {
    setLoading: (loading: boolean) => setLoadingFunction(set, loading),
    updateCart: (cart: CartType) => updateCartFunction(set, cart),
    setError: (error: any) => setErrorFunction(set, error),
    updateMetaTotal: (total: number) => updateMetaTotalFunction(set, total),
    updateCartMetaTotalAmmount: (total: string) => updateCartMetaTotalAmmountFunction(set, total),
    updateSingleTicketQty: (qty: number[]) => updateSingleTicketQtyFunction(set, qty),
  }

  const {
    setLoading,
    updateCart,
    setError,
    updateMetaTotal,
    updateCartMetaTotalAmmount,
    updateSingleTicketQty,
  } = stateActions;

  // # Get Cart method fetches the cart and updates the whole cart state
  const getCart = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/cart`, { credentials: 'include' });
      const cart: CartType = await response.json();
      setLoading(false);

      const ticketQtyArray = cart.data.tickets.map((ticket) => ticket.qty);
      updateMetaTotal(cart.meta.total_count)
      updateCartMetaTotalAmmount(cart.meta.total_price)
      updateSingleTicketQty(ticketQtyArray)

      updateCart(cart);
      return cart;
    } catch (err) {
      setLoading(false);
      setError(true);
      console.warn(err);
    }
  };

  // # Add To Cart method adds tickets to the cart and updates the whole cart state
  const addToCart = async (tickets: Array<AddToCartTicketType>) => {
    setLoading(true);

    let cart: CartType | null = null;

    for (const ticket of tickets) {
      if (!ticket.qty) continue;

      const body = {
        items: [
          {
            ticket_id: ticket.ticketId,
            date_id: ticket.dateId,
            qty: ticket.qty,
          },
        ],
      };

      try {
        const result = await httpRequest('POST', `${apiBaseUrl}/cart?returnGet=1`, body);

        cart = await result.json();

      } catch (err) {
        setLoading(false);
        setError(true);
        console.warn(err);
      }
    }

    if (cart) {
      setLoading(false);
      updateMetaTotal(cart.meta.total_count)
      updateCart(cart);
      googleDataLayer.logAddToCart(cart.data, tickets);

    } else {
      setLoading(false);
    }
  };

  // # Set Ticket Qty method updates the qty of a single ticket in the cart and updates the whole cart state
  const setTicketQty = async (cartItemId: string, newQty: number) => {
  setLoading(true);

  if (cartItemId) {
    const body = {
      items: [
        {
          cart_item_id: cartItemId,
          qty: newQty,
        },
      ],
    };

    try {
      const result = await httpRequest('PUT', `${apiBaseUrl}/cart?returnGet=1`, body);
      const cart: CartType = await result.json();

      setLoading(false);


      set((state) => produce(state, (draft) => {
        if (cart.data.tickets.some(t => t.cart_item_id === cartItemId)) {
          const updatedTicket = cart.data.tickets.find(t => t.cart_item_id === cartItemId);
          if (updatedTicket) {
            draft.ticketQtys = [updatedTicket.qty];
          }
        }

        draft.cartMetaTotalAmmount = cart.meta.total_price;
        draft.cartMetaTotalCount = cart.meta.total_count;
        draft.cart = cart;
      }));

    } catch (err) {
      setLoading(false);
      setError(true);
      console.warn(err);
    }
  } else {
    setLoading(false);
  }
};

  // # Remove Cart Item removes a single ticket from the cart and updates the whole cart state
  const removeCartItem = async (ticketId: string, dateId: string | null, qty: number) => {
    setLoading(true);

    const cart = await getCart();

    dateId = ''

    if (!cart) return;

    const cartItem = cart.data.tickets.find(t => t.ticket_id === ticketId && (t.data.date_id ?? '' == dateId))

    if (cartItem) {

      const body = { qty }

      try {

        const result = await httpRequest('DELETE', `${apiBaseUrl}/cart/${cartItem.cart_item_id}?returnGet=1`, body);
        const cart: CartType = await result.json();

        setLoading(false);
        updateCartMetaTotalAmmount(cart.meta.total_price)
        updateMetaTotal(cart.meta.total_count)
        updateCart(cart);

      } catch (err) {

        setLoading(false);
        setError(true);
        console.warn(err);
      }
    }

  };

  // # Clear Cart removes all tickets from the cart and updates the whole cart state
  const removeAllFromCart = async (ticketIds: Array<string>, dateOrYearStr?: string) => {
    setLoading(true);

    const cart = await getCart();

    if (!cart) return;

    let newCart: CartType | null = null;
    const removedTickets: Array<{ ticket: CartTicket, qty: number}> = []

    let cartTicketsToRemove: Array<CartTicket> = cart.data.tickets.filter(t => ticketIds.includes(t.ticket_id))

    if (dateOrYearStr) {
      cartTicketsToRemove = cartTicketsToRemove.filter(t => (t.data.date ?? '').includes(dateOrYearStr))
    }

    for (const cartTicket of cartTicketsToRemove) {
      const url = `${apiBaseUrl}/cart/${cartTicket.cart_item_id}?returnGet=1`
      const result = await httpRequest('DELETE', url)
      newCart = await result.json()

      removedTickets.push({ ticket: cartTicket, qty: cartTicket.qty})
    }


    if (newCart) {
      updateCartMetaTotalAmmount(newCart.meta.total_price)
      updateMetaTotal(newCart.meta.total_count)
      updateCart(newCart)
      setLoading(false)
    } else {
      setLoading(false)
    }
  }

  // # Clear Cart removes all tickets from the cart and updates the whole cart state
  const clearCart = async () => {

    try {

      const result = await httpRequest('DELETE', `${apiBaseUrl}/cart?returnGet=1`);
      const cart: CartType = await result.json();

      setLoading(false);
      updateCart(cart);

    } catch (err) {

      setLoading(false);
      setError(true);
      console.warn(err);
    }
  }


  return {
    cart: CartDefaultState,
    cartMetaTotalCount: 0,
    cartMetaTotalAmmount: '0',
    ticketQtys: [],
    cartLoading: false,
    error: false,
    getCart,
    addToCart,
    setTicketQty,
    removeCartItem,
    removeAllFromCart,
    clearCart,
  };
  },
  ) as unknown as StateCreator<State>
);

export default useCartStore;